import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  X, Check, Plus, ArrowLeft, ArrowRight, MessageCircle, Play, Pause, ChevronLeft, ChevronRight,
  Salad, Home, Bot, Users, Sparkles, Dumbbell, Clock, Sun,
  TrendingUp, Target, Shield, Smartphone, MonitorSmartphone,
  Instagram, Quote, Sunrise, Sunset, Moon, Coffee, LogIn,
} from "lucide-react";

/**
 * Faisal Flex — flexibility program landing page (RTL Arabic, calofay-inspired).
 * Route: /flex
 * Palette: #FFFFFF light-dominant · #FF6A00 orange accent · #0A0A0A for impact blocks
 */

import { SHOW_HOME_WORKOUT, SHOW_QUIZ } from "../config/featureFlags";
import { stampFunnel } from "@/lib/funnels";

const ORANGE = "#FF6A00";
const ORANGE_SOFT = "#FFF4EA";
// Primary CTA destination — flip SHOW_QUIZ in featureFlags.js to toggle whether
// visitors land in the quiz first or skip straight to checkout.
const CTA_HREF = SHOW_QUIZ
  ? "/flex/quiz?funnel=flex&utm_source=flex&utm_medium=landing"
  : "/checkout?funnel=flex&utm_source=flex&utm_medium=landing";

// Renumber bonus cards when the home-workout bonus (card 01) is hidden:
// 02→01, 03→02, 04→03, 05→04, 06→05
const bonusNum = (n) => (SHOW_HOME_WORKOUT ? n : String(parseInt(n, 10) - 1).padStart(2, "0"));

// Set this to a Cloudflare Stream video UID to play a hero video that autoplays,
// loops muted, and takes over the hero visual (mobile + desktop). When empty,
// the coach image is shown instead.
// UID `02b4a03d2396178172f488f08c14d52a` is the new intro video ("فيديو المقدمة"
// — 73s) copied to Cloudflare Stream on 2026-08-01 from the customer-assets .mov
// so mobile visitors get adaptive-bitrate HLS instead of a 137MB blob download.
const HERO_VIDEO_CF_UID = "02b4a03d2396178172f488f08c14d52a";
const HERO_VIDEO_POSTER = "/coach-hero-1.png";

// Legacy: direct URL kept for reference only — Cloudflare Stream now hosts
// the intro video (see HERO_VIDEO_CF_UID above), so this fallback is unused.
const HERO_VIDEO_DIRECT_SRC = "";

// Custom poster frame seconds — Cloudflare Stream picks that exact second
// as the still image shown before the user taps play. Tweak these values
// to change which frame is displayed.
const HERO_POSTER_TIME_SEC = 2;   // Hero video thumbnail second
const PHONE_POSTER_TIME_SEC = 3;  // Phone mockup video thumbnail second

// Real app screenshots — cycled inside the phone mockup
const APP_SHOTS = [
  { src: "/app-shots/recipes.png", label: "مكتبة الوصفات" },
  { src: "/app-shots/ai-chat.png", label: "مدرب الذكاء الاصطناعي" },
  { src: "/app-shots/courses.png", label: "برنامج التمارين" },
  { src: "/app-shots/community.png", label: "مجتمع فيصل" },
  { src: "/app-shots/dashboard.png", label: "لوحتك الرئيسية" },
];

// ─────────────────────────────────────────────────────────────
// Reveal on scroll (Fade Up), respects prefers-reduced-motion
// ─────────────────────────────────────────────────────────────
// One SHARED IntersectionObserver is reused across every Reveal on the page.
// Previously each of the 43 Reveal components created its own observer, which
// piled up layout+style recalcs on iPhone Safari and made overlay animations
// (drawer, modals) feel sluggish. A single observer is ~40× cheaper.
const _revealSubscribers = new WeakMap();
const _revealObserver =
  typeof window !== "undefined" && "IntersectionObserver" in window
    ? new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (e.isIntersecting) {
              const cb = _revealSubscribers.get(e.target);
              if (cb) {
                cb();
                _revealObserver.unobserve(e.target);
                _revealSubscribers.delete(e.target);
              }
            }
          }
        },
        { threshold: 0.15 },
      )
    : null;

function Reveal({ children, delay = 0, className = "", as: Tag = "div" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setShown(true); return; }
    const el = ref.current;
    if (!el || !_revealObserver) { setShown(true); return; }
    _revealSubscribers.set(el, () => setShown(true));
    _revealObserver.observe(el);
    return () => {
      _revealObserver.unobserve(el);
      _revealSubscribers.delete(el);
    };
  }, []);
  return (
    <Tag
      ref={ref}
      className={`transition-[opacity,transform] duration-700 ease-out ${shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

// ─────────────────────────────────────────────────────────────
// Small pill badge — used above every section headline (calofay-style)
// ─────────────────────────────────────────────────────────────
function Pill({ children, tone = "orange" }) {
  const style =
    tone === "orange"
      ? { background: ORANGE_SOFT, color: ORANGE, borderColor: `${ORANGE}30` }
      : { background: "rgba(255,255,255,0.08)", color: "#fff", borderColor: "rgba(255,255,255,0.15)" };
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] md:text-xs font-semibold"
      style={style}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone === "orange" ? ORANGE : "#fff" }} />
      {children}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// Header — sandwich menu on right + CTA on left
// ─────────────────────────────────────────────────────────────
function Header() {
  return (
    <>
      {/* Fixed header — logo (right in RTL) + CTA (left).
          The mobile hamburger drawer was removed after iOS Safari benchmarks
          showed 6-second stalls per open even with every known Safari
          optimization applied. On-page anchor scroll (via section links in
          the body) is the primary in-page navigation instead. */}
      <header dir="rtl" className="fixed top-0 inset-x-0 z-40 bg-[#0A0A0A]/95 border-b border-white/5" data-testid="flex-header">
        <div className="mx-auto max-w-6xl h-14 md:h-16 px-4 md:px-8 flex items-center gap-3">
          {/* Logo — sits at the start (right in RTL). Padding gives it a bit
              of breathing room from the edge now that the hamburger is gone. */}
          <a href="#top" className="flex items-center gap-2 pr-1 md:pr-2" data-testid="flex-logo">
            <span className="text-base md:text-lg font-black text-white tracking-tight whitespace-nowrap">
              برنامج <span style={{ color: ORANGE }}>المرونة</span>
            </span>
          </a>

          {/* Header actions — sit at the end (left in RTL). We keep the
              members-login link intentionally small and low-contrast so it
              doesn't compete with the primary "ابدأ الآن" CTA that we want
              new visitors to click. Existing subscribers already know what
              they're looking for and will find it easily. */}
          <div className="mr-auto flex items-center gap-2 md:gap-3">
            {/* Members login — luxurious gold-on-black pill. Elevated visual
                weight because returning subscribers looking for this CTA
                deserve first-class treatment. Same styling on mobile + desktop
                (only paddings/text size scale down). */}
            <Link
              to="/login"
              className="inline-flex items-center gap-1 md:gap-1.5 rounded-full px-3 md:px-5 py-1.5 md:py-2.5 text-[11px] md:text-sm font-bold text-white bg-black border transition hover:brightness-125 shadow-[0_0_12px_rgba(212,175,55,0.35)] whitespace-nowrap"
              style={{ borderColor: "#D4AF37" }}
              data-testid="flex-header-login"
            >
              <LogIn className="w-3 h-3 md:w-3.5 md:h-3.5" style={{ color: "#D4AF37" }} />
              <span style={{ background: "linear-gradient(135deg,#F4E4A6 0%,#D4AF37 50%,#B8860B 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                دخول المشتركين
              </span>
            </Link>
            <Link
              to={CTA_HREF}
              className="inline-flex items-center gap-1 md:gap-1.5 rounded-full px-3 md:px-5 py-1.5 md:py-2.5 text-[11px] md:text-sm font-bold text-black hover:brightness-110 transition whitespace-nowrap"
              style={{ background: ORANGE }}
              data-testid="flex-header-cta"
            >
              ابدأ الآن
              <ArrowLeft className="w-3 h-3 md:w-3.5 md:h-3.5" />
            </Link>
          </div>
        </div>
      </header>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Floating WhatsApp bubble — bottom-right, opens WhatsApp with
// a prefilled question addressed to the number the owner shared.
// ─────────────────────────────────────────────────────────────
const WA_NUMBER = "966539098620";
const WA_MESSAGE = "عندي سؤال عن منصة التمارين";
function WhatsAppBubble() {
  const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_MESSAGE)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="تواصل عبر واتساب"
      data-testid="whatsapp-bubble"
      className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full grid place-items-center shadow-xl shadow-emerald-900/30 hover:scale-105 active:scale-95 transition-transform"
      style={{ background: "#25D366" }}
    >
      <svg viewBox="0 0 32 32" className="w-7 h-7" fill="#fff" aria-hidden="true">
        <path d="M19.11 17.28c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.3-.76.96-.93 1.15-.17.2-.34.22-.64.07-.3-.15-1.25-.46-2.39-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.34.44-.51.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.91-2.19-.24-.58-.48-.5-.66-.51l-.56-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.21 5.09 4.5.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35zM16 3a13 13 0 0 0-11.11 19.68L3 29l6.55-1.72A13 13 0 1 0 16 3zm0 23.6a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-3.88 1.02 1.04-3.77-.25-.4A10.6 10.6 0 1 1 16 26.6z"/>
      </svg>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────
// Primary CTA
// ─────────────────────────────────────────────────────────────
function CTA({ children = "ابدأ برنامج المرونة الآن", tone = "orange", size = "lg", testid, href = CTA_HREF }) {
  const sizeCls = size === "sm" ? "px-5 py-3 text-sm" : "px-7 py-4 text-base md:text-lg";
  const styles = {
    orange: { background: ORANGE, color: "#000", boxShadow: `0 12px 32px -14px ${ORANGE}` },
    dark: { background: "#0A0A0A", color: "#fff" },
    ghost: { background: "#fff", color: "#0A0A0A", border: "1px solid rgba(0,0,0,0.08)" },
  };
  // `Link` gives us client-side SPA navigation — a full page reload was
  // costing ~1 s of JS re-parse on iPhone Safari on every CTA tap, which
  // felt exactly like "slow internet". With `Link`, the transition to the
  // quiz is under 100 ms.
  return (
    <Link
      to={href}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-bold hover:brightness-105 active:scale-[0.98] transition ${sizeCls}`}
      style={styles[tone]}
    >
      {children}
      <ArrowLeft className="w-4 h-4" />
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────
// Coach image with silhouette fallback
// ─────────────────────────────────────────────────────────────
function CoachImage({ src, alt = "الكوتش فيصل كردي", className = "", tag, tone = "dark" }) {
  const [broken, setBroken] = useState(false);
  if (broken || !src) {
    return (
      <div className={`relative overflow-hidden ${className}`}>
        <div
          className="absolute inset-0"
          style={{
            background: tone === "dark"
              ? "linear-gradient(135deg,#171717 0%,#0A0A0A 60%,#171717 100%)"
              : "linear-gradient(135deg,#F5F5F5 0%,#EAEAEA 60%,#F5F5F5 100%)",
          }}
        />
        <div
          className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full blur-3xl opacity-25"
          style={{ background: ORANGE }}
        />
        <svg viewBox="0 0 200 300" className={`relative w-full h-full ${tone === "dark" ? "text-white/25" : "text-black/15"}`} fill="currentColor" aria-hidden>
          <circle cx="100" cy="60" r="28" />
          <path d="M60 110 h80 q20 0 20 30 v50 q0 20 -20 20 h-80 q-20 0 -20 -20 v-50 q0 -30 20 -30 z" />
          <rect x="70" y="205" width="18" height="80" rx="9" />
          <rect x="112" y="205" width="18" height="80" rx="9" />
        </svg>
        {tag && (
          <div className={`absolute bottom-3 right-3 text-[10px] ${tone === "dark" ? "text-white/60 bg-black/40" : "text-black/60 bg-white/70"} px-2 py-1 rounded-md backdrop-blur-sm`}>
            {tag}
          </div>
        )}
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setBroken(true)} className={className} />;
}

// ─────────────────────────────────────────────────────────────
// Phone mockup — iPhone-style frame playing a real app-demo video
// on Cloudflare Stream, with a pulsing "tap to watch" hint. Tap
// opens the video in a full modal with sound + controls.
// ─────────────────────────────────────────────────────────────
const APP_EXPLAINER_CF_UID = "d7e64e18b1efd7821c8e9a94c171fc79";

function PhoneMockup({
  scale = 1,
  uid = APP_EXPLAINER_CF_UID,
  directSrc = null,
  posterSec = PHONE_POSTER_TIME_SEC,
  title = "شرح التطبيق",
  testid = "flex-phone",
  videoTestid = "phone-inline-video",
  hint = "اضغط للمشاهدة",
}) {
  return (
    <div
      className="relative mx-auto"
      style={{ width: `${248 * scale}px` }}
      data-testid={testid}
    >
      {/* Ambient orange glow */}
      <div className="absolute -inset-8 rounded-[3rem] blur-3xl opacity-25" style={{ background: ORANGE }} aria-hidden />

      {/* Frame */}
      <div className="relative rounded-[2.6rem] p-[3px] bg-gradient-to-b from-white/25 via-white/10 to-white/5 shadow-2xl">
        <div className="rounded-[2.5rem] p-[3px] bg-[#111]">
          <div className="relative rounded-[2.3rem] overflow-hidden bg-black" style={{ aspectRatio: "9/19.5" }}>
            {/* Dynamic Island */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-20 pointer-events-none" />

            {/* Inline video — tap to play/pause, sound on automatically, no native controls */}
            <InlineStreamVideo
              uid={uid}
              directSrc={directSrc}
              posterSec={posterSec}
              title={title}
              testid={videoTestid}
              playBtnSize={66}
              iconSize={26}
            />
          </div>
        </div>
      </div>

      {/* External nudge — arrow + text pointing to the phone */}
      {hint && (
        <div className="mt-4 flex items-center justify-center gap-2 select-none">
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-bold shadow-lg"
            style={{ background: ORANGE, color: "#0A0A0A" }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
            {hint}
          </span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Inline Cloudflare Stream video with:
//   • native <video> element (HLS via hls.js, or Safari's native HLS)
//   • sound ON automatically after the user taps play (unmute + play
//     happen in the same click handler → passes iOS/Chrome autoplay policy)
//   • NO native controls — tapping the video body toggles play/pause
//   • custom orange play button + rings while paused
// Used for both the hero and the phone mockup marketing videos.
// ─────────────────────────────────────────────────────────────
function InlineStreamVideo({ uid, directSrc, posterSec = 2, title, testid, playBtnSize = 84, iconSize = 32 }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(true);

  // Direct video src (e.g. .mov/.mp4 URL) takes precedence over Cloudflare Stream HLS.
  const isDirect = Boolean(directSrc);
  const poster = isDirect
    ? undefined
    : `https://customer-4e0w7u7jlrhakp84.cloudflarestream.com/${uid}/thumbnails/thumbnail.jpg?time=${posterSec}s&height=1080`;
  const hlsUrl = isDirect
    ? null
    : `https://customer-4e0w7u7jlrhakp84.cloudflarestream.com/${uid}/manifest/video.m3u8`;

  // Attach the video source once the user starts playback.
  useEffect(() => {
    if (!started) return;
    const v = videoRef.current;
    if (!v) return;
    // Direct src (mov/mp4) — just point <video src> at it.
    if (isDirect) { v.src = directSrc; return; }
    let hls;
    if (v.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari / iOS — native HLS
      v.src = hlsUrl;
    } else {
      // Chrome / Firefox / Android Chrome — hls.js
      import("hls.js").then(({ default: Hls }) => {
        if (Hls.isSupported()) {
          hls = new Hls({ enableWorker: true });
          hls.loadSource(hlsUrl);
          hls.attachMedia(v);
          hlsRef.current = hls;
        } else {
          v.src = hlsUrl;
        }
      }).catch(() => { v.src = hlsUrl; });
    }
    return () => { if (hlsRef.current) { hlsRef.current.destroy(); hlsRef.current = null; } };
  }, [started, hlsUrl, isDirect, directSrc]);

  const startPlayback = () => {
    const v = videoRef.current;
    if (!v) return;
    // Critical: unmute + play must happen inside the same tap event
    // for iOS/Chrome to accept audible playback.
    v.muted = false;
    v.volume = 1;
    setStarted(true);
    setPaused(false);
    // Give the effect a tick to attach the source, then play.
    Promise.resolve().then(() => v.play().catch(() => {}));
  };

  const togglePlay = () => {
    if (!started) { startPlayback(); return; }
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPaused(false); }
    else { v.pause(); setPaused(true); }
  };

  return (
    <div className="absolute inset-0" data-testid={testid}>
      <video
        ref={videoRef}
        poster={poster}
        preload="metadata"
        playsInline
        webkit-playsinline="true"
        onClick={togglePlay}
        onPlay={() => setPaused(false)}
        onPause={() => setPaused(true)}
        onEnded={() => setPaused(true)}
        className="absolute inset-0 w-full h-full object-contain bg-black cursor-pointer"
        title={title}
      />
      {/* Play/Pause overlay — visible only when paused. Tapping it plays.
          When playing, this whole layer is gone so the video handles taps directly. */}
      {paused && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 w-full h-full grid place-items-center group cursor-pointer"
          aria-label={started ? "استئناف الفيديو" : "تشغيل الفيديو"}
          data-testid={testid ? `${testid}-play` : undefined}
        >
          <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
          <span className="relative grid place-items-center">
            <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `${ORANGE}44`, width: playBtnSize + 12, height: playBtnSize + 12 }} />
            <span className="absolute inset-0 rounded-full animate-ping [animation-delay:0.6s]" style={{ background: `${ORANGE}33`, width: playBtnSize + 12, height: playBtnSize + 12 }} />
            <span
              className="relative grid place-items-center rounded-full shadow-2xl group-hover:scale-110 active:scale-95 transition-transform"
              style={{ width: playBtnSize, height: playBtnSize, background: ORANGE, boxShadow: `0 20px 40px -10px ${ORANGE}` }}
            >
              <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="#0A0A0A" aria-hidden><path d="M8 5v14l11-7z"/></svg>
            </span>
          </span>
        </button>
      )}
    </div>
  );
}

// Hero visual — renders inside a vertical iPhone-style mockup identical to
// the app-explainer video in the features section. Plays the intro video
// from Cloudflare Stream (HERO_VIDEO_CF_UID) with adaptive-bitrate HLS.
function HeroVisual() {
  if (HERO_VIDEO_CF_UID) {
    return (
      <PhoneMockup
        scale={1.15}
        uid={HERO_VIDEO_CF_UID}
        posterSec={HERO_POSTER_TIME_SEC}
        title="فيديو المقدمة"
        testid="flex-hero-phone"
        videoTestid="flex-hero-video"
        hint="اضغط لتشغيل المقدمة"
      />
    );
  }
  return (
    <CoachImage
      src="/coach-hero-1.png"
      className="w-full aspect-[4/5] rounded-3xl border border-white/10 object-cover shadow-2xl"
      tag="الكوتش فيصل"
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Section headline block (calofay-style)
// ─────────────────────────────────────────────────────────────
function SectionHead({ pill, title, sub, align = "right", tone = "light" }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-right";
  const titleCls = tone === "dark" ? "text-white" : "text-[#0A0A0A]";
  const subCls = tone === "dark" ? "text-white/60" : "text-[#404040]";
  return (
    <div className={`max-w-3xl ${alignCls}`}>
      <Pill tone={tone === "dark" ? "white" : "orange"}>{pill}</Pill>
      <h2 className={`mt-4 text-[26px] md:text-5xl font-black leading-[1.2] md:leading-[1.15] tracking-tight ${titleCls}`}>{title}</h2>
      {sub && <p className={`mt-4 text-[15px] md:text-lg leading-relaxed ${subCls}`}>{sub}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// FAQ accordion
// ─────────────────────────────────────────────────────────────
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="flex-faq-item">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 text-right px-5 md:px-6 py-4 md:py-5 hover:bg-white/[0.02]">
        <span className="text-white font-semibold text-sm md:text-base">{q}</span>
        <span className="w-8 h-8 rounded-full grid place-items-center shrink-0 transition-transform" style={{ background: `${ORANGE}20`, color: ORANGE, transform: open ? "rotate(45deg)" : "none" }}>
          <Plus className="w-4 h-4" />
        </span>
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <p className="px-5 md:px-6 pb-5 pt-0 text-white/70 text-sm md:text-base leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Animated counter
// ─────────────────────────────────────────────────────────────
function Counter({ to = 8000, className = "", suffix = "" }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setN(to); return; }
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        io.disconnect();
        const start = performance.now();
        const dur = 1600;
        const step = (t) => {
          const p = Math.min(1, (t - start) / dur);
          setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.4 });
    io.observe(el); return () => io.disconnect();
  }, [to]);
  return <span ref={ref} className={className}>{n.toLocaleString("en-US")}{suffix}</span>;
}

// ─────────────────────────────────────────────────────────────
// Testimonial (legacy — kept for API compat)
// ─────────────────────────────────────────────────────────────
function Testimonial({ name, city, quote }) {
  return (
    <div className="rounded-2xl border border-black/[0.08] bg-white p-6 shadow-sm">
      <Quote className="w-6 h-6 mb-3" style={{ color: ORANGE }} />
      <p className="text-[#0A0A0A] leading-relaxed text-sm md:text-base">{quote}</p>
      <div className="mt-4 flex items-center gap-2.5 text-xs">
        <div className="w-8 h-8 rounded-full grid place-items-center font-bold text-white" style={{ background: "#171717" }}>{name[0]}</div>
        <div><div className="font-semibold text-[#0A0A0A] text-sm">{name}</div><div className="text-[#737373]">{city}</div></div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Image Carousel (auto-play + pause/play + prev/next + dots)
// Used for real client WhatsApp testimonials and before/after photos.
// ─────────────────────────────────────────────────────────────
function ImageCarousel({ images, intervalMs = 4500, aspect = "aspect-[3/4]", label = "" }) {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const total = images.length;
  useEffect(() => {
    if (!playing || hovered || total < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % total), intervalMs);
    return () => clearInterval(id);
  }, [playing, hovered, intervalMs, total]);

  const go = (dir) => setIdx((i) => (i + dir + total) % total);

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`carousel-${label}`}
    >
      {/* Frame */}
      <div className={`relative w-full ${aspect} rounded-3xl overflow-hidden border border-white/10 bg-black/60 shadow-2xl`}>
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${label} ${i + 1}`}
            loading="lazy"
            className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            data-testid={`carousel-${label}-image-${i}`}
          />
        ))}

        {/* Prev / Next buttons */}
        <button
          type="button"
          aria-label="السابق"
          onClick={() => go(-1)}
          className="absolute top-1/2 -translate-y-1/2 right-3 md:right-4 w-10 h-10 md:w-11 md:h-11 rounded-full grid place-items-center bg-black/60 hover:bg-black/80 text-white backdrop-blur border border-white/10 transition"
          data-testid={`carousel-${label}-prev`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        <button
          type="button"
          aria-label="التالي"
          onClick={() => go(1)}
          className="absolute top-1/2 -translate-y-1/2 left-3 md:left-4 w-10 h-10 md:w-11 md:h-11 rounded-full grid place-items-center bg-black/60 hover:bg-black/80 text-white backdrop-blur border border-white/10 transition"
          data-testid={`carousel-${label}-next`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {/* Play / Pause */}
        <button
          type="button"
          aria-label={playing ? "إيقاف مؤقت" : "تشغيل"}
          onClick={() => setPlaying((v) => !v)}
          className="absolute bottom-3 left-3 md:bottom-4 md:left-4 w-10 h-10 md:w-11 md:h-11 rounded-full grid place-items-center bg-black/60 hover:bg-black/80 text-white backdrop-blur border border-white/10 transition"
          data-testid={`carousel-${label}-toggle`}
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 translate-x-0.5" />}
        </button>

        {/* Counter */}
        <div className="absolute bottom-3 right-3 md:bottom-4 md:right-4 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white text-xs font-semibold px-2.5 py-1">
          {idx + 1} / {total}
        </div>
      </div>

      {/* Dots */}
      <div className="mt-4 flex items-center justify-center gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`الشريحة ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8" : "w-2 hover:w-4"}`}
            style={{ background: i === idx ? ORANGE : "rgba(255,255,255,0.25)" }}
            data-testid={`carousel-${label}-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
export default function FlexLandingPage() {
  // Tag this visitor with the "flex" funnel so the checkout lead + email/WhatsApp
  // sequences route to the flex marketing track. Persists via cookie for 180 days.
  useEffect(() => { stampFunnel("flex"); }, []);

  // SEO
  useEffect(() => {
    const prev = document.title;
    document.title = "برنامج المرونة — تمرن براحة من البيت";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
    })();
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute("content", "برنامج المرونة — 3 مستويات، أكثر من ساعة ونصف من التدريبات، بدون أدوات. تمرن من البيت وحسّن مدى حركتك بالتدريج.");
    return () => { document.title = prev; if (prevDesc) meta.setAttribute("content", prevDesc); };
  }, []);

  return (
    <div
      dir="rtl"
      id="top"
      className="min-h-screen bg-white text-[#0A0A0A] font-[system-ui,'IBM_Plex_Sans_Arabic','Tajawal',sans-serif]"
      data-testid="flex-landing"
    >
      <Header />

      {/* Fixed header spacer */}
      <div className="pt-14 md:pt-16" />

      {/* ═════════════════════════ 1. HERO — DARK w/ orange glow ═════════════════════════ */}
      <section id="hero" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full blur-[130px] opacity-30" style={{ background: ORANGE }} aria-hidden />
        <div className="absolute -bottom-40 -right-40 w-[420px] h-[420px] rounded-full blur-[130px] opacity-15" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-6xl px-5 md:px-8 pt-10 md:pt-14 pb-16 md:pb-24 relative">
          <div className="grid md:grid-cols-[1.1fr_1fr] gap-10 md:gap-14 items-center">
            <div>
              <Reveal>
                <Pill tone="white">برنامج المرونة الرسمي من الكوتش فيصل كردي</Pill>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="mt-5 text-[32px] leading-[1.2] md:text-6xl md:leading-[1.15] font-black tracking-tight text-white">
                  جسمك فيه شد وتيبس؟
                  <br />
                  خلّه يتحرك <span style={{ color: ORANGE }}>براحة من جديد</span>
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-4 md:mt-5 text-white/70 text-[15px] md:text-lg leading-relaxed max-w-xl">
                  برنامج المرونة يساعدك تحسّن مرونتك ومدى حركتك من البيت، من خلال تمارين واضحة ومتدرجة تستهدف الجزء العلوي والسفلي من الجسم. بدون أدوات، بدون نادي، وبدون ما تحتار كل يوم وش تتمرن.
                </p>
              </Reveal>

              {/* MOBILE-ONLY hero video — moved above the fold, right after the subhead
                  so the coach's face validates the offer before benefits and CTA.
                  Desktop keeps the side-by-side visual (see right column below). */}
              <Reveal delay={200} className="md:hidden">
                <div className="relative mt-6">
                  <div className="absolute top-4 right-4 z-10 rounded-full bg-white text-[#0A0A0A] text-[11px] font-bold px-3 py-1.5 shadow-lg flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: ORANGE }} />
                    +<Counter to={8000} /> مشترك
                  </div>
                  <HeroVisual />
                </div>
              </Reveal>

              <Reveal delay={220}>
                <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
                  {[
                    "أكثر من ساعة ونصف من تمارين المرونة",
                    "3 مستويات: مبتدئ، متوسط، متقدم",
                    "تمارين تشمل الجسم كامل",
                    "مناسب للرجال والنساء",
                    "تواصل خاص مع الكوتش فيصل",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm md:text-base text-white/90">
                      <span className="mt-1 w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: ORANGE }}>
                        <Check className="w-2.5 h-2.5 text-black stroke-[3]" />
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
              <Reveal delay={300}>
                <div className="mt-8 flex flex-col items-start gap-3">
                  <CTA testid="flex-hero-cta">ابدأ برنامج المرونة الآن</CTA>
                  <p className="text-white/50 text-xs md:text-sm flex items-center gap-2">
                    <MonitorSmartphone className="w-4 h-4" />
                    تمرّن من جوالك، اللابتوب، التابلت أو التلفزيون
                  </p>
                </div>
              </Reveal>
            </div>

            <Reveal delay={200} className="hidden md:block">
              <div className="relative">
                <div className="absolute top-4 right-4 z-10 rounded-full bg-white text-[#0A0A0A] text-[11px] md:text-xs font-bold px-3 py-1.5 shadow-lg flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: ORANGE }} />
                  +<Counter to={8000} /> مشترك
                </div>
                <HeroVisual />
                <div className="absolute -bottom-6 -left-6 md:-left-10 hidden sm:block">
                  <PhoneMockup scale={0.85} />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═════════════════════════ 2. PROBLEM — DARK w/ orange glow ═════════════════════════ */}
      <section id="problem" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-24 relative">
          <Reveal>
            <SectionHead
              pill="المشكلة"
              title="تحس جسمك مو مثل أول؟"
              sub="يمكن تحس بشد في الرقبة والأكتاف، أو تيبس في الظهر والحوض، أو إن حركتك صارت محدودة وقت التمرين. ويمكن جربت تمارين عشوائية من الإنترنت، لكن للحين مو عارف من وين تبدأ."
              tone="dark"
            />
          </Reveal>

          <div className="mt-10 md:mt-12 grid md:grid-cols-2 gap-4">
            {[
              { n: "01", t: "شد في الرقبة والأكتاف", d: "خصوصاً من الجلسة الطويلة أمام الجوال أو اللابتوب." },
              { n: "02", t: "تيبس في الظهر والحوض", d: "يمنعك من الانحناء أو الجلوس براحة." },
              { n: "03", t: "محدودية في مدى الحركة", d: "تحس إن جسمك مو قابل يتحرك بحرية." },
              { n: "04", t: "حيرة بين التمارين العشوائية", d: "ما تعرف من وين تبدأ ولا وش يناسبك." },
            ].map((c, i) => (
              <Reveal key={c.n} delay={i * 80}>
                <div className="relative rounded-2xl bg-white/[0.03] border border-white/10 p-6 h-full hover:border-[color:var(--o)] hover:-translate-y-1 transition-all" style={{ "--o": ORANGE }}>
                  <div className="flex items-start gap-5">
                    <span className="text-4xl md:text-5xl font-black shrink-0 leading-none" style={{ color: ORANGE }}>{c.n}</span>
                    <div className="pt-1">
                      <div className="font-bold text-white text-lg">{c.t}</div>
                      <div className="mt-1.5 text-sm text-white/60 leading-relaxed">{c.d}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Solution card */}
          <Reveal delay={120}>
            <div className="mt-12 rounded-3xl bg-white/[0.03] border border-white/10 p-8 md:p-12 relative overflow-hidden">
              <div className="absolute -top-16 -left-16 w-72 h-72 rounded-full blur-[100px] opacity-25" style={{ background: ORANGE }} />
              <div className="relative">
                <Pill tone="white">الحل</Pill>
                <h3 className="mt-4 text-2xl md:text-4xl font-black leading-tight text-white">
                  مو ناقصك حماس — ناقصك <span style={{ color: ORANGE }}>برنامج واضح</span> تمشي عليه
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed max-w-2xl">
                  مع برنامج المرونة، تبدأ من مستواك الحالي، وتطبق التمارين بالتدريج، وتتطور خطوة بخطوة بدون استعجال أو تعقيد.
                </p>
                <div className="mt-8">
                  <Link to={CTA_HREF} className="inline-flex items-center gap-2 rounded-full font-bold text-black px-6 py-3 text-sm md:text-base" style={{ background: ORANGE }} data-testid="flex-problem-cta">
                    ابدأ من مستواك <ArrowLeft className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ 3. HOW IT WORKS — DARK w/ orange glow ═════════════════════════ */}
      <section id="how" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-24 relative">
          <Reveal>
            <SectionHead
              pill="كيف يعمل"
              title="خطتك تبدأ بخطوات بسيطة"
              sub="برنامج المرونة يساعدك تبدأ من مستواك، وتتطور بالتدريج بدون تعقيد."
              tone="dark"
            />
          </Reveal>

          <div className="mt-10 md:mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { n: 1, t: "اختر مستواك", d: "مبتدئ، متوسط أو متقدم — تبدأ من اللي يناسبك.", icon: Target },
              { n: 2, t: "افتح التمرين", d: "تمارين مرتبة وواضحة تقدر ترجع لها في أي وقت.", icon: Play },
              { n: 3, t: "تمرّن بالتدريج", d: "بدون أدوات، من البيت، بالسرعة اللي تناسبك.", icon: TrendingUp },
              { n: 4, t: "لاحظ تطور حركتك", d: "بعد أسابيع، ترى الفرق في مرونتك وراحة جسمك.", icon: Sparkles },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 90}>
                <div className="relative rounded-2xl bg-white/[0.03] border border-white/10 p-6 h-full hover:border-[color:var(--o)] hover:-translate-y-1 transition-all" style={{ "--o": ORANGE }}>
                  <div className="absolute top-4 left-4 text-6xl font-black leading-none text-white/[0.06]">{s.n}</div>
                  <div className="relative">
                    <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                      <s.icon className="w-5 h-5" />
                    </div>
                    <div className="text-xs font-bold" style={{ color: ORANGE }}>الخطوة {s.n}</div>
                    <div className="mt-1 text-lg font-bold text-white">{s.t}</div>
                    <div className="mt-2 text-sm text-white/60 leading-relaxed">{s.d}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═════════════════════════ 4. PROGRAM CONTENT — DARK w/ orange glow ═════════════════════════ */}
      <section id="program" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-24 relative">
          <Reveal>
            <SectionHead pill="محتوى البرنامج" title="وش داخل برنامج المرونة؟" sub="برنامج واضح ومتدرج يساعدك تحسّن مرونتك بدون تعقيد." tone="dark" />
          </Reveal>

          <div className="mt-10 md:mt-12 grid md:grid-cols-2 gap-5">
            <Reveal>
              <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 h-full hover:border-[color:var(--o)] transition" style={{ "--o": ORANGE }}>
                <div className="text-6xl md:text-7xl font-black leading-none" style={{ color: ORANGE }}>+1.5</div>
                <div className="mt-2 text-xl font-bold text-white">ساعات تدريبية</div>
                <p className="mt-2 text-white/60 text-sm md:text-base">تمارين مرتبة تقدر ترجع لها وتتمرن في الوقت اللي يناسبك.</p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 h-full hover:border-[color:var(--o)] transition" style={{ "--o": ORANGE }}>
                <div className="text-xl font-bold text-white">3 مستويات متدرجة</div>
                <p className="mt-2 text-white/60 text-sm md:text-base">تبدأ من مستواك وتتطور بالتدريج بدون ضغط.</p>
                <div className="mt-5 flex items-center gap-2">
                  {["مبتدئ", "متوسط", "متقدم"].map((l, idx) => (
                    <div key={l} className="flex-1">
                      <div className="rounded-lg text-center text-xs py-2 font-semibold" style={{
                        background: idx === 1 ? `${ORANGE}20` : "rgba(255,255,255,0.03)",
                        color: idx === 1 ? ORANGE : "rgba(255,255,255,0.7)",
                        border: `1px solid ${idx === 1 ? ORANGE : "rgba(255,255,255,0.1)"}`,
                      }}>{l}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between text-[10px] text-white/50 px-1">
                  <span>ابدأ هنا</span>
                  <ArrowLeft className="w-3 h-3" />
                  <span>تطور بالتدريج</span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={160}>
              <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 h-full hover:border-[color:var(--o)] transition" style={{ "--o": ORANGE }}>
                <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                  <Target className="w-5 h-5" />
                </div>
                <div className="text-xl font-bold text-white">تمارين تشمل الجسم كامل</div>
                <p className="mt-2 text-white/60 text-sm">البرنامج يستهدف الجزء العلوي والسفلي بما فيها:</p>
                <ul className="mt-3 grid grid-cols-2 gap-1.5 text-sm text-white/85">
                  {["الرقبة والأكتاف", "الظهر والعمود الفقري", "الحوض والورك", "الفخذين والرجلين", "الركب والكاحل"].map((t) => (
                    <li key={t} className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 shrink-0" style={{ color: ORANGE }} />{t}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={240}>
              <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 h-full hover:border-[color:var(--o)] transition" style={{ "--o": ORANGE }}>
                <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                  <Home className="w-5 h-5" />
                </div>
                <div className="text-xl font-bold text-white">بدون أدوات</div>
                <p className="mt-2 text-white/60 text-sm md:text-base">افتح البرنامج وتمرّن من البيت بدون أجهزة أو اشتراك نادي.</p>
                <div className="mt-4 rounded-xl border border-white/10 bg-black/40 p-3 flex items-center gap-2 text-xs text-white/70">
                  <Smartphone className="w-4 h-4" style={{ color: ORANGE }} />
                  جوال · لابتوب · تابلت · تلفزيون
                </div>
              </div>
            </Reveal>
          </div>

          {/* Coach chat strip */}
          <Reveal delay={200}>
            <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex flex-col md:flex-row items-center gap-4">
              <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 border-2" style={{ borderColor: ORANGE }}>
                <CoachImage src="/coach-hero-2.png" className="w-full h-full object-cover" />
              </div>
              <div className="text-center md:text-right flex-1">
                <div className="font-bold text-white">عندك سؤال عن تمرين؟</div>
                <div className="text-sm text-white/60 mt-0.5">تقدر تتواصل بشكل خاص مع الكوتش فيصل من داخل التطبيق.</div>
              </div>
              <Link to={CTA_HREF} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white border border-white/15 hover:bg-white/[0.05]" data-testid="flex-coach-chat">
                <MessageCircle className="w-4 h-4" style={{ color: ORANGE }} />
                تواصل مع الكوتش
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ 5. BENEFITS — DARK w/ orange glow ═════════════════════════ */}
      <section id="benefits" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-24 relative">
          <Reveal>
            <SectionHead pill="الفوائد" title="وش ممكن تلاحظ مع الاستمرار؟" tone="dark" />
          </Reveal>

          <div className="mt-10 md:mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: TrendingUp, t: "حركة أريح", b: "الجلوس، القيام، الانحناء والتمرين يصير أسهل عليك." },
              { icon: Shield, t: "شد وتيبس أقل", b: "خصوصاً في المناطق اللي تتأثر من الجلسة الطويلة." },
              { icon: Target, t: "مدى حركة أفضل", b: "تؤدي حركاتك وتمارينك بمدى أوسع وتحكم أعلى." },
              { icon: Dumbbell, t: "أداء أفضل في التمارين", b: "جسمك يصير أكثر استعداداً للحركة والتمرين." },
              { icon: Sparkles, t: "احتمالية إصابات أقل بإذن الله", b: "تحسين المرونة يقلل الشد والإصابات المرتبطة بضعف الاستعداد." },
              { icon: Users, t: "ثقة أكبر في جسمك", b: "تسوي حركات كانت صعبة عليك براحة وتحكم أكثر." },
            ].map((b, i) => (
              <Reveal key={b.t} delay={i * 80}>
                <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 h-full hover:border-[color:var(--o)] hover:-translate-y-1 transition-all" style={{ "--o": ORANGE }}>
                  <div className="w-11 h-11 rounded-xl grid place-items-center mb-4" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                    <b.icon className="w-5 h-5" />
                  </div>
                  <div className="text-lg font-bold text-white">{b.t}</div>
                  <div className="mt-2 text-sm text-white/60 leading-relaxed">{b.b}</div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={280}>
            <div className="mt-12 rounded-3xl bg-white text-[#0A0A0A] p-8 md:p-12 text-center">
              <p className="text-xl md:text-3xl font-black leading-snug">
                الهدف مو إنك تصير لاعب جمباز — الهدف إنك <span style={{ color: ORANGE }}>تتحرك براحة</span> بدون ما تحس إن جسمك معطلك.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ 6. COMPARISON — DARK TABLE ═════════════════════════ */}
      <section id="comparison" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-5xl px-5 md:px-8 py-14 md:py-24 relative">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <Pill tone="white">الفرق</Pill>
              <h2 className="mt-4 text-[26px] md:text-5xl font-black leading-[1.25] md:leading-tight text-white">الفرق بين إنك تخمّن… وإنك تتحرك بخطة</h2>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className="mt-12 rounded-3xl overflow-hidden border border-white/10 bg-white/[0.02]">
              <table className="w-full text-sm md:text-base border-collapse">
                <thead>
                  <tr>
                    <th className="p-4 md:p-6 text-right font-bold text-white/60 bg-white/[0.04] border-b border-white/10 w-2/5">
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full grid place-items-center bg-white/10"><X className="w-3.5 h-3.5 text-white/60" /></span>
                        بدون برنامج المرونة
                      </div>
                    </th>
                    <th className="p-4 md:p-6 text-right font-bold border-b border-white/10 relative" style={{ background: `${ORANGE}18`, color: ORANGE }}>
                      <div className="flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full grid place-items-center text-black" style={{ background: ORANGE }}><Check className="w-3.5 h-3.5 stroke-[3]" /></span>
                        مع برنامج المرونة
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["تمارين عشوائية من الإنترنت", "خطة واضحة ومتدرجة"],
                    ["ما تعرف من وين تبدأ", "3 مستويات — تبدأ من مستواك"],
                    ["مو متأكد أي مستوى يناسبك", "اختيار المستوى المناسب لك"],
                    ["تمارين ما تشمل الجسم كامل", "تمارين للجزء العلوي والسفلي"],
                    ["شد وتيبس متكرر بدون تحسّن", "شد أقل ومدى حركة أفضل"],
                    ["تفقد الحماس وتوقف بسرعة", "استمرارية بدون استعجال"],
                    ["ما تشوف تطورك بوضوح", "تلاحظ تطورك مع الاستمرار"],
                    ["ما فيه دعم لما تحتاج توجيه", "تواصل خاص مع الكوتش فيصل"],
                  ].map(([without, wth], i) => (
                    <tr key={i} className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition">
                      <td className="p-4 md:p-5 text-white/50 align-top">
                        <div className="flex items-start gap-2.5">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-white/30 shrink-0" />
                          <span>{without}</span>
                        </div>
                      </td>
                      <td className="p-4 md:p-5 text-white align-top border-r border-white/10">
                        <div className="flex items-start gap-2.5">
                          <span className="mt-1 w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: ORANGE }}>
                            <Check className="w-2.5 h-2.5 text-black stroke-[3]" />
                          </span>
                          <span className="font-medium">{wth}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>

          <p className="mt-8 text-center text-white/50 text-sm md:text-base max-w-xl mx-auto">
            مو المطلوب تكون مثالي كل يوم — المطلوب تعرف وين أنت، وتقدر ترجع لخطتك بسهولة.
          </p>
        </div>
      </section>

      {/* ═════════════════════════ 7. APP + BONUSES — DARK ═════════════════════════ */}
      <section id="features" className="relative overflow-hidden bg-[#0A0A0A]">
        <div className="absolute -top-40 right-1/2 translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[130px] opacity-15" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-24 relative">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-[26px] md:text-5xl font-black leading-[1.25] md:leading-tight text-white">اشتراكك يفتح لك <span style={{ color: ORANGE }}>تطبيق متكامل</span></h2>
              <p className="mt-4 text-white/60 text-base md:text-lg">
                {SHOW_HOME_WORKOUT ? "6" : "5"} مزايا إضافية <span className="font-bold" style={{ color: ORANGE }}>كلها مشمولة مجاناً</span> مع اشتراكك في برنامج المرونة
              </p>
            </div>
          </Reveal>

          {/* Phone Mockup — mobile only, sits ABOVE card 01 so the visual leads. Desktop shows it inside the grid below. */}
          <div className="lg:hidden mt-10 flex justify-center" data-testid="mobile-mockup-lead">
            <PhoneMockup />
          </div>

          {/* 01 — Dumbbell workout (mobile: appears BELOW the mockup; desktop: full-width above the grid) */}
          {SHOW_HOME_WORKOUT && (
          <Reveal delay={80}>
            <div className="mt-6 lg:mt-12 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8 relative overflow-hidden hover:border-[color:var(--o)] transition" style={{ "--o": ORANGE }}>
              <span className="absolute top-4 left-4 rounded-full text-[10px] font-black px-2 py-0.5 text-black z-10" style={{ background: ORANGE }}>مجاني</span>
              <div className="grid md:grid-cols-[auto_1fr] gap-5 md:gap-8 items-start">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl grid place-items-center font-black text-2xl md:text-3xl shrink-0" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                  01
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Dumbbell className="w-5 h-5 shrink-0" style={{ color: ORANGE }} />
                    <h3 className="text-lg md:text-2xl font-bold text-white">برنامج التمارين المنزلية بالدامبل</h3>
                  </div>
                  <p className="mt-2 text-white/60 text-sm md:text-base leading-relaxed">
                    تمارين فيديو تطبقها من البيت — تطوّر قوتك ولياقتك وتحكمك في حركة جسمك بدون الحاجة تروح النادي.
                  </p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                    {[
                      "تقوية العضلات وشد الجسم",
                      "تحسين اللياقة والتوازن والثبات",
                      "دعم صحة العظام والمفاصل",
                      "تقوية العضلات المحيطة بالمفاصل",
                      "زيادة معدل الحرق ودعم نزول الوزن",
                      "تقليل احتمالية الإصابات أثناء التمرين",
                      "تحسين الصحة واللياقة بشكل عام",
                    ].map((t) => (
                      <li key={t} className="flex items-start gap-2.5 text-sm text-white/85">
                        <span className="mt-1 w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: ORANGE }}>
                          <Check className="w-2.5 h-2.5 text-black stroke-[3]" />
                        </span>
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>
          )}

          {/* 02-05 — Grid with phone in the center */}
          <div className="mt-6 grid lg:grid-cols-[1fr_320px_1fr] gap-6 md:gap-8 items-center">
            <div className="space-y-4 order-2 lg:order-1">
              {[
                { n: bonusNum("02"), icon: Salad, t: "مكتبة وصفات تتجدد أسبوعياً", b: "وجبات، حلويات ومشروبات صحية — أفكار متجددة تنهي حيرة كل يوم." },
                { n: bonusNum("03"), icon: Sparkles, t: "استخراج وصفات إنستغرام وتيك توك", b: "انسخ رابط المقطع، واستخرج المكونات والخطوات واحفظها داخل مكتبتك." },
              ].map((f, i) => (
                <Reveal key={f.n} delay={i * 90}>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex items-start gap-4 hover:border-[color:var(--o)] transition relative" style={{ "--o": ORANGE }}>
                    <span className="absolute top-3 left-3 rounded-full text-[10px] font-black px-2 py-0.5 text-black" style={{ background: ORANGE }}>مجاني</span>
                    <div className="w-11 h-11 rounded-lg grid place-items-center shrink-0 font-black text-lg" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                      {f.n}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <f.icon className="w-4 h-4 shrink-0" style={{ color: ORANGE }} />
                        <div className="font-bold text-white">{f.t}</div>
                      </div>
                      <div className="text-sm text-white/60 mt-1 leading-relaxed">{f.b}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="order-1 lg:order-2 hidden lg:flex justify-center">
              <PhoneMockup />
            </div>

            <div className="space-y-4 order-3">
              {[
                { n: bonusNum("04"), icon: Bot, t: "مدرب الذكاء الاصطناعي", b: "اسأله عن المرونة، السعرات، البروتين، الوصفات واللياقة — إجابة مباشرة داخل التطبيق." },
                { n: bonusNum("05"), icon: Users, t: "مجتمع فيصل للتمارين", b: "شارك تجربتك واسأل وتفاعل مع باقي المشتركين — ما راح تحس إنك لحالك." },
              ].map((f, i) => (
                <Reveal key={f.n} delay={i * 90}>
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 flex items-start gap-4 hover:border-[color:var(--o)] transition relative" style={{ "--o": ORANGE }}>
                    <span className="absolute top-3 left-3 rounded-full text-[10px] font-black px-2 py-0.5 text-black" style={{ background: ORANGE }}>مجاني</span>
                    <div className="w-11 h-11 rounded-lg grid place-items-center shrink-0 font-black text-lg" style={{ background: `${ORANGE}20`, color: ORANGE }}>
                      {f.n}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <f.icon className="w-4 h-4 shrink-0" style={{ color: ORANGE }} />
                        <div className="font-bold text-white">{f.t}</div>
                      </div>
                      <div className="text-sm text-white/60 mt-1 leading-relaxed">{f.b}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* 06 — Coach chat */}
          <Reveal delay={220}>
            <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-6 md:p-8 flex flex-col md:flex-row items-center gap-5 relative">
              <span className="absolute top-4 left-4 rounded-full text-[10px] font-black px-2 py-0.5 text-black" style={{ background: ORANGE }}>مجاني</span>
              <div className="w-14 h-14 rounded-2xl grid place-items-center font-black text-2xl shrink-0" style={{ background: `${ORANGE}20`, color: ORANGE }}>{bonusNum("06")}</div>
              <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 border-2" style={{ borderColor: ORANGE }}>
                <CoachImage src="/coach-hero-2.png" className="w-full h-full object-cover" />
              </div>
              <div className="text-center md:text-right flex-1">
                <div className="flex items-center gap-2 justify-center md:justify-start">
                  <MessageCircle className="w-4 h-4 shrink-0" style={{ color: ORANGE }} />
                  <div className="font-bold text-white text-lg">تواصل خاص مع الكوتش فيصل</div>
                </div>
                <div className="text-sm text-white/60 mt-1">
                  عندك سؤال خاص عن البرنامج أو التمارين؟ أرسل سؤالك بشكل خاص للكوتش فيصل وتحصل على التوجيه المناسب.
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={280}>
            <div className="mt-10 text-center">
              <p className="text-xl md:text-3xl font-black text-white leading-tight">
                كل برامجك وأدواتك في <span style={{ color: ORANGE }}>حساب واحد</span> وتطبيق واحد
              </p>
              <p className="mt-3 text-sm md:text-base text-white/60">
                وتقدر تستخدمه من الآيفون، أندرويد، التابلت، اللابتوب أو التلفزيون.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═════════════════════════ 8. COACH + RESULTS — DARK w/ orange glow ═════════════════════════ */}
      <section id="results" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-24 relative">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div className="relative">
                <div className="absolute -top-4 -right-4 text-[110px] md:text-[160px] font-black leading-none text-white/[0.06] pointer-events-none select-none">+20</div>
                <CoachImage src="/coach-hero-2.png" tone="dark" className="relative w-full aspect-[4/5] rounded-3xl object-cover shadow-2xl border border-white/10" tag="الكوتش فيصل كردي" />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <Pill tone="white">الخبرة</Pill>
                <h2 className="mt-4 text-[26px] md:text-5xl font-black leading-[1.25] md:leading-tight text-white">
                  خبرة أكثر من <span style={{ color: ORANGE }}>20 سنة</span> في الحركة والمرونة
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="mt-5 text-white/70 leading-relaxed">
                  بدأت رحلة الكوتش فيصل كردي مع الفنون القتالية والبطولات الرياضية، وهناك اكتشف إن القوة لحالها ما تكفي. الجسم يحتاج مرونة ومدى حركة جيد عشان يقدر يتحرك ويؤدي التمارين بشكل أفضل.
                </p>
                <p className="mt-3 text-white/70 leading-relaxed">
                  بعد سنوات من التدريب والدراسة والتطبيق، طوّر الكوتش فيصل برنامجًا متدرجًا يناسب المبتدئين والرياضيين. واليوم، انضم إلى البرنامج أكثر من <span className="font-black text-white"><Counter to={8000} /></span> مشترك.
                </p>
              </Reveal>
              <Reveal delay={180}>
                <div className="mt-7 grid grid-cols-2 gap-4">
                  <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                    <div className="text-3xl md:text-4xl font-black" style={{ color: ORANGE }}>+8,000</div>
                    <div className="text-sm text-white/60 mt-1">مشترك في برنامج المرونة</div>
                  </div>
                  <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                    <div className="text-3xl md:text-4xl font-black" style={{ color: ORANGE }}>3</div>
                    <div className="text-sm text-white/60 mt-1">مستويات تدريبية</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Results */}
          <Reveal delay={100}>
            <div className="mt-16 text-center max-w-2xl mx-auto">
              <Pill tone="white">النتائج</Pill>
              <h3 className="mt-4 text-2xl md:text-4xl font-black leading-tight text-white">الفرق يبان في حركتك</h3>
              <p className="mt-3 text-white/60 text-sm md:text-base">يمكن تلاحظ تطورك لما:</p>
            </div>
          </Reveal>

          <Reveal delay={140}>
            <ul className="mt-6 max-w-2xl mx-auto grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {[
                "تقوم من الأرض بسهولة أكثر",
                "تحرك أكتافك وظهرك براحة أكبر",
                "تؤدي تمارينك بمدى حركة أفضل",
                "تحس إن جسمك أخف وأقل تيبساً",
                "تسوي حركات كانت صعبة عليك من قبل",
                "تقل احتمالية الشد والإصابات بإذن الله",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm md:text-base text-white/85">
                  <span className="mt-1 w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: ORANGE }}>
                    <Check className="w-2.5 h-2.5 text-black stroke-[3]" />
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Real client testimonials — WhatsApp screenshots carousel */}
          <Reveal delay={80}>
            <div className="mt-10 md:mt-14 grid lg:grid-cols-[1fr_1fr] gap-8 md:gap-12 items-start">
              {/* WhatsApp testimonials carousel */}
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" style={{ color: ORANGE }} />
                  <h4 className="text-white font-bold text-base md:text-lg">شهادات من مشتركين البرنامج</h4>
                </div>
                <ImageCarousel
                  label="testimonials"
                  aspect="aspect-[3/4]"
                  intervalMs={4500}
                  images={["/testimonials/t1.png", "/testimonials/t2.png", "/testimonials/t3.png", "/testimonials/t4.png", "/testimonials/t5.png"]}
                />
              </div>

              {/* Before / After results carousel */}
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" style={{ color: ORANGE }} />
                  <h4 className="text-white font-bold text-base md:text-lg">نتائج حقيقية — قبل / بعد</h4>
                </div>
                <ImageCarousel
                  label="results"
                  aspect="aspect-[3/2]"
                  intervalMs={5500}
                  images={["/results/r1.png", "/results/r2.png", "/results/r3.png"]}
                />
              </div>
            </div>
          </Reveal>

          <p className="mt-8 text-xs text-white/40 text-center max-w-xl mx-auto">
            النتائج تختلف من شخص لشخص حسب مستوى المرونة الحالي والالتزام والاستمرارية.
          </p>
        </div>
      </section>

      {/* ═════════════════════════ 9. OFFER + FAQ — DARK w/ orange glow ═════════════════════════ */}
      <section id="offer" className="relative overflow-hidden bg-[#0A0A0A] text-white">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-[130px] opacity-20" style={{ background: ORANGE }} aria-hidden />
        <div className="mx-auto max-w-4xl px-5 md:px-8 py-14 md:py-24 relative">
          <Reveal>
            <div className="rounded-3xl bg-white/[0.03] border border-white/10 p-6 md:p-10">
              <div className="text-center">
                <Pill tone="white">العرض</Pill>
                <h2 className="mt-4 text-2xl md:text-4xl font-black leading-tight text-white">كل اللي تحتاجه في اشتراك واحد</h2>
              </div>
              <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {[
                  "برنامج المرونة الكامل",
                  "أكثر من ساعة ونصف من تمارين المرونة",
                  "3 مستويات متدرجة",
                  "تمارين للجزء العلوي والسفلي من الجسم",
                  ...(SHOW_HOME_WORKOUT ? ["برنامج التمارين المنزلية بالدامبل"] : []),
                  "مكتبة وصفات تتجدد أسبوعياً",
                  "استخراج وصفات إنستغرام وتيك توك",
                  "مدرب الذكاء الاصطناعي",
                  "مجتمع فيصل للتمارين",
                  "تواصل خاص مع الكوتش",
                  "الاستخدام على الجوال، اللابتوب والتلفزيون",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm md:text-base">
                    <span className="mt-1 w-4 h-4 rounded-full grid place-items-center shrink-0" style={{ background: ORANGE }}>
                      <Check className="w-2.5 h-2.5 text-black stroke-[3]" />
                    </span>
                    <span className="text-white/90">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 rounded-2xl bg-black/40 p-5 md:p-6 text-center border border-white/10">
                <div className="text-xs text-white/60 mb-3">استخدم كود الخصم</div>
                <div className="inline-block rounded-lg px-4 py-2 text-black font-black text-lg md:text-xl tracking-widest" style={{ background: ORANGE }}>8000</div>
                <div className="mt-6">
                  <CTA testid="flex-offer-cta">اشترك في برنامج المرونة الآن</CTA>
                </div>
                <div className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-white/60">
                  <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" style={{ color: ORANGE }} /> دفع آمن عبر Stripe</span>
                  <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" style={{ color: ORANGE }} /> دخول مباشر بعد الاشتراك</span>
                  <span className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" style={{ color: ORANGE }} /> يعمل على جميع الأجهزة</span>
                </div>
              </div>
            </div>
          </Reveal>

          {/* FAQ */}
          <div id="faq" className="mt-16 scroll-mt-24">
            <Reveal>
              <div className="text-center mb-8 max-w-2xl mx-auto">
                <Pill tone="white">الأسئلة الشائعة</Pill>
                <h3 className="mt-4 text-2xl md:text-4xl font-black text-white">أسئلة قبل ما تبدأ</h3>
              </div>
            </Reveal>
            <div className="space-y-3">
              {[
                { q: "هل البرنامج مناسب للمبتدئين؟", a: "نعم، البرنامج فيه 3 مستويات متدرجة، وتقدر تبدأ من المستوى المناسب لك وتتطور خطوة بخطوة." },
                { q: "هل لازم أكون مرن قبل ما أبدأ؟", a: "لا أبدًا. أنت تدخل البرنامج عشان تطور مرونتك بالتدريج، حتى لو كانت مرونتك محدودة حاليًا." },
                { q: "هل أحتاج أدوات لبرنامج المرونة؟", a: "لا، برنامج المرونة ما يحتاج أدوات، وتقدر تتمرن من البيت." },
                { q: "هل البرنامج يشمل الجسم كامل؟", a: "نعم، البرنامج يشمل تمارين للجزء العلوي والسفلي من الجسم." },
                { q: "هل أحصل على برنامج المرونة بس؟", a: SHOW_HOME_WORKOUT
                    ? "لا، اشتراكك يشمل أيضًا برنامج التمارين المنزلية بالدامبل، مكتبة الوصفات، استخراج الوصفات، مدرب الذكاء الاصطناعي، المجتمع والتواصل الخاص مع الكوتش."
                    : "لا، اشتراكك يشمل أيضًا مكتبة الوصفات، استخراج الوصفات، مدرب الذكاء الاصطناعي، المجتمع والتواصل الخاص مع الكوتش." },
                { q: "هل المزايا والبونصات عليها رسوم إضافية؟", a: "لا، جميع المزايا المذكورة مشمولة داخل اشتراك برنامج المرونة بحسب الخطة اللي تختارها." },
                { q: "هل وصفات المكتبة تتجدد؟", a: "نعم، تتم إضافة وصفات صحية وشهية ومشروبات جديدة بشكل أسبوعي." },
                ...(SHOW_HOME_WORKOUT ? [{ q: "هل برنامج التمارين المنزلية يحتاج أدوات؟", a: "البرنامج يعتمد على تمارين باستخدام الدامبل. تقدر تختار الوزن المناسب لمستواك وتتمرن من البيت." }] : []),
                { q: "كيف أستخرج وصفة من إنستغرام أو تيك توك؟", a: "انسخ رابط المقطع وحطه داخل أداة استخراج الوصفات، وراح يحاول التطبيق استخراج المكونات وطريقة التحضير عشان تقدر تراجعها وتحفظها." },
                { q: "وش أقدر أسأل مدرب الذكاء الاصطناعي؟", a: "تقدر تسأله عن التمارين، المرونة، السعرات، البروتين، الوصفات، التغذية واللياقة بشكل عام." },
                { q: "هل أقدر أتواصل مع الكوتش بشكل خاص؟", a: "نعم، تقدر ترسل أسئلتك الخاصة المتعلقة بالبرنامج والتمارين للكوتش فيصل." },
                { q: "هل يشتغل على جميع الأجهزة؟", a: "نعم، تقدر تستخدمه على الآيفون، أندرويد، التابلت، اللابتوب والتلفزيون، حسب الجهاز والمتصفح." },
                { q: "هل البرنامج يعتبر علاجًا؟", a: "لا، البرنامج تدريبي وتعليمي وما يعتبر علاجًا طبيًا. إذا عندك إصابة أو ألم شديد أو حالة صحية، استشر طبيبًا أو مختصًا قبل ما تبدأ." },
              ].map((f, i) => (
                <Reveal key={f.q} delay={i * 40}><FAQItem q={f.q} a={f.a} /></Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═════════════════════════ 11. FINAL CTA ═════════════════════════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <CoachImage src="/coach-hero-1.png" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/95 via-black/80 to-black/60" />
          <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full blur-[130px] opacity-30" style={{ background: ORANGE }} />
        </div>
        <div className="relative mx-auto max-w-4xl px-5 md:px-8 py-20 md:py-28 text-center">
          <Reveal><h2 className="text-[26px] md:text-5xl font-black leading-[1.25] md:leading-tight text-white">جسمك ما راح يصير أكثر مرونة من <span style={{ color: ORANGE }}>الانتظار</span></h2></Reveal>
          <Reveal delay={100}>
            <p className="mt-5 text-white/75 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              ما تحتاج تبدأ بحركات صعبة، ولا تقضي ساعات في التمرين. ابدأ من مستواك، تمرّن بالتدريج، وامشِ على خطة واضحة تساعدك تتحرك براحة أكثر.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {["مرونة أكثر", "تيبس أقل", "حركة أريح في يومك"].map((t) => (
                <span key={t} className="rounded-full border border-white/20 bg-white/5 backdrop-blur px-4 py-2 text-sm md:text-base font-semibold text-white">{t}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={260}>
            <div className="mt-10">
              <CTA testid="flex-final-cta">ابدأ برنامج المرونة الآن</CTA>
              <p className="mt-4 text-sm text-white/60 max-w-md mx-auto">وافتح جميع برامج ومزايا التطبيق من نفس الحساب.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Floating WhatsApp bubble */}
      <WhatsAppBubble />

      {/* Footer — trimmed */}
      <footer className="bg-[#0A0A0A] border-t border-white/10" data-testid="flex-footer">
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-10 text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <span className="text-lg font-black text-white">برنامج <span style={{ color: ORANGE }}>المرونة</span></span>
          </div>

          {/* Member login CTA — pill style, prominent */}
          <div>
            <Link
              to="/login"
              data-testid="footer-login"
              className="inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold bg-white text-[#0A0A0A] hover:bg-[color:var(--o)] hover:text-black transition-colors"
              style={{ "--o": ORANGE }}
            >
              <ArrowLeft className="w-4 h-4 rtl:-scale-x-100" />
              تسجيل الدخول للمشتركين
            </Link>
          </div>

          <p className="text-sm text-white/70">
            Support Email: <a href="mailto:support@faisalflex.com" className="hover:text-white transition-colors" data-testid="footer-email">support@faisalflex.com</a>
          </p>
          <p className="text-xs text-white/60 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/legal#privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy</Link>
            <span className="text-white/20">|</span>
            <Link to="/legal#terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms</Link>
            <span className="text-white/20">|</span>
            <Link to="/legal#earnings" className="hover:text-white transition-colors" data-testid="footer-earnings">Earnings Disclaimer</Link>
          </p>
          <p className="text-xs text-white/40">Faisal fitness LLC © 2026. All Rights Reserved</p>
        </div>
      </footer>
    </div>
  );
}
